// WatchPanel — pinned-to-top widget that surfaces "things to watch + reconcile"
// across the Dashboard, Financials, and any overview screen. Pulls open issues
// + overdue invoices + stuck jobs + failed webhook leads into one prioritized
// list so the owner sees the actionable stuff first, not the vanity metrics.
import React, { useEffect, useState } from 'react';
import { G, styles, api, fmtUsd } from '../theme.js';
import { Btn, Badge, Spinner } from '../ui.jsx';

export function WatchPanel({ go = () => {}, toast = () => {}, compact = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      // Pull from every source in parallel — if any one fails it doesn't kill the others.
      const [issues, invoices, jobs, leads] = await Promise.all([
        api('/api/issues?status=open').catch(() => []),
        api('/api/invoices').catch(() => []),
        api('/api/jobs').catch(() => []),
        api('/api/lead-capture/recent').catch(() => ({ leads: [] })),
      ]);
      const collected = [];
      // Issues from /api/issues — already structured
      for (const i of issues) {
        const overdue = i.dueAt && new Date(i.dueAt) < new Date();
        collected.push({
          key: `issue:${i.id}`,
          icon: i.kind === 'callback' ? '📞' : i.kind === 'reminder' ? '⏰' : '⚠️',
          severity: i.severity || 'normal',
          title: i.title,
          sub: i.description || '',
          meta: overdue ? `Overdue · was due ${new Date(i.dueAt).toLocaleDateString()}` : i.dueAt ? `Due ${new Date(i.dueAt).toLocaleDateString()}` : null,
          go: () => go('issues'),
        });
      }
      // Overdue invoices — anything past due_date and not paid
      const today = new Date(); today.setHours(0,0,0,0);
      const overdueInv = (Array.isArray(invoices) ? invoices : []).filter(inv => {
        if (inv.status === 'paid') return false;
        if (!inv.due) return false;
        return new Date(inv.due) < today;
      }).sort((a,b) => new Date(a.due) - new Date(b.due));
      const totalOverdue = overdueInv.reduce((s,inv) => {
        const lines = inv.lines || [];
        return s + lines.reduce((a,l)=>a + (Number(l.qty)||0)*(Number(l.price)||0), 0) * (1 + (inv.taxRate||7)/100) - (inv.paid||0);
      }, 0);
      if (overdueInv.length) {
        collected.push({
          key: 'overdue-invoices',
          icon: '💸',
          severity: totalOverdue > 5000 ? 'high' : 'normal',
          title: `${overdueInv.length} overdue invoice${overdueInv.length===1?'':'s'} — ${fmtUsd(totalOverdue)} unpaid`,
          sub: `Oldest: ${overdueInv[0].contact || overdueInv[0].name || 'unknown'} (${new Date(overdueInv[0].due).toLocaleDateString()})`,
          go: () => go('invoices'),
        });
      }
      // Stuck jobs — En Route / On Site for > 8 hours, or Booked > 3 days past scheduled_date
      const stuck = (Array.isArray(jobs) ? jobs : []).filter(j => {
        if (['En Route','On Site','Dispatched'].includes(j.status) && j.updatedAt) {
          const h = (Date.now() - new Date(j.updatedAt).getTime()) / 3600000;
          return h > 8;
        }
        if (j.status === 'Booked' && j.scheduledDate) {
          const d = (Date.now() - new Date(j.scheduledDate).getTime()) / 86400000;
          return d > 3;
        }
        return false;
      });
      if (stuck.length) {
        collected.push({
          key: 'stuck-jobs',
          icon: '🛠️',
          severity: 'normal',
          title: `${stuck.length} job${stuck.length===1?'':'s'} stuck — needs status update`,
          sub: stuck.slice(0,2).map(j => `${j.name} (${j.status})`).join(' · '),
          go: () => go('jobs'),
        });
      }
      // Failed webhook leads — landed in lead_inbox with an error or unprocessed
      const failedLeads = (leads.leads || []).filter(l => l.error || (!l.processed && l.raw_payload));
      if (failedLeads.length) {
        collected.push({
          key: 'failed-leads',
          icon: '📥',
          severity: 'high',
          title: `${failedLeads.length} webhook lead${failedLeads.length===1?'':'s'} need attention`,
          sub: failedLeads[0].error || 'Landed but contact not yet created — open Integrations to retry',
          go: () => go('integrations'),
        });
      }
      // Sort by severity: critical → high → normal → low
      const order = { critical: 0, high: 1, normal: 2, low: 3 };
      collected.sort((a,b) => (order[a.severity] ?? 2) - (order[b.severity] ?? 2));
      setItems(collected);
    } catch (e) {
      console.warn('[WatchPanel]', e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const onFocus = () => load();
    window.addEventListener('tradeops:tab-focus', onFocus);
    const t = setInterval(load, 5 * 60 * 1000);
    return () => { window.removeEventListener('tradeops:tab-focus', onFocus); clearInterval(t); };
  }, []);

  if (loading && !items.length) return null;
  if (!loading && !items.length) return null; // hide the empty state — no clutter

  const critical = items.filter(i => i.severity === 'critical' || i.severity === 'high').length;
  const headerColor = critical > 0 ? G.red : G.orange;

  return <div style={{ ...styles.card, padding:0, marginBottom:18, borderColor: headerColor + '66', overflow:'hidden' }}>
    <div onClick={()=>setCollapsed(!collapsed)} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 18px', cursor:'pointer', borderBottom: collapsed ? 'none' : `1px solid ${G.border}` }}>
      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
        <span style={{ fontSize:18 }}>{critical > 0 ? '🚨' : '👀'}</span>
        <strong style={{ fontSize:14 }}>Things to watch + reconcile</strong>
        <Badge c={headerColor} bg={headerColor+'22'}>{items.length}</Badge>
        {critical > 0 && <Badge c={G.red} bg={G.red+'22'}>{critical} urgent</Badge>}
      </div>
      <span style={{ color:G.textMuted, fontSize:12 }}>{collapsed ? '▸ expand' : '▾ collapse'}</span>
    </div>
    {!collapsed && (
      <div style={{ padding:'6px 8px' }}>
        {items.slice(0, compact ? 4 : 12).map(it => {
          const c = it.severity === 'critical' ? G.red : it.severity === 'high' ? G.orange : it.severity === 'low' ? G.textMuted : G.yellow;
          return <div key={it.key} onClick={it.go}
            onMouseEnter={e=>e.currentTarget.style.background = G.input}
            onMouseLeave={e=>e.currentTarget.style.background = 'transparent'}
            style={{ padding:'10px 12px', borderRadius:8, display:'flex', alignItems:'center', gap:12, cursor:'pointer' }}>
            <span style={{ fontSize:18 }}>{it.icon}</span>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontWeight:600, fontSize:13 }}>{it.title}</div>
              {it.sub && <div style={{ fontSize:11, color:G.textMuted, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{it.sub}</div>}
              {it.meta && <div style={{ fontSize:11, color:c, fontWeight:600, marginTop:2 }}>{it.meta}</div>}
            </div>
            <span style={{ color:c, fontSize:12, fontWeight:700 }}>→</span>
          </div>;
        })}
        {items.length > (compact ? 4 : 12) && (
          <div style={{ textAlign:'center', padding:'6px 0' }}>
            <Btn sz="xs" v="ghost" onClick={()=>go('issues')}>View all {items.length} →</Btn>
          </div>
        )}
      </div>
    )}
  </div>;
}
